import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FaTimes, FaCheck } from 'react-icons/fa';
import './ImageCropper.css';

/**
 * Image cropper using ReactCrop rectangle selection.
 * Same experience on desktop and mobile — drag corners/edges to adjust,
 * drag inside the selection to move it.
 *
 * Props:
 *   imageSrc    – data URL, blob URL, or proxied URL
 *   aspectRatio – number (e.g. 2.5/3.5) or null for free crop
 *   title       – modal title
 *   onCrop      – (file: File, previewUrl: string) => void
 *   onCancel    – () => void
 */
const ImageCropper = ({ imageSrc, aspectRatio = null, title = 'Crop Image', subtitle = null, cancelLabel = 'Cancel', onCrop, onCancel }) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  // Free crop lets the user drag each edge independently — useful for
  // graded slabs and other non-standard ratios. Default off so the
  // standard 2.5:3.5 card ratio is the easy path.
  const [freeCrop, setFreeCrop] = useState(false);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    // Default crop: 80% centered, free aspect
    let cropW = 80;
    let cropH = 80;
    if (aspectRatio) {
      const imgAspect = width / height;
      if (aspectRatio > imgAspect) {
        cropW = 80;
        cropH = (cropW / aspectRatio) * (width / height);
        if (cropH > 90) { cropH = 90; cropW = cropH * aspectRatio * (height / width); }
      } else {
        cropH = 80;
        cropW = cropH * aspectRatio * (height / width);
        if (cropW > 90) { cropW = 90; cropH = (cropW / aspectRatio) * (width / height); }
      }
    }
    setCrop({
      unit: '%',
      width: cropW,
      height: cropH,
      x: (100 - cropW) / 2,
      y: (100 - cropH) / 2,
    });
  }, [aspectRatio]);

  const handleApply = useCallback(() => {
    const image = imgRef.current;
    if (!image || !crop || !crop.width || !crop.height) return;

    // Crop is guaranteed to be in `%` (we normalize it in onChange). Apply the
    // percentages directly to the natural image dimensions — this avoids any
    // dependency on the current layout / display size, so it's robust to
    // measurement quirks in `image.width`.
    const naturalW = image.naturalWidth;
    const naturalH = image.naturalHeight;

    const sx = (crop.x / 100) * naturalW;
    const sy = (crop.y / 100) * naturalH;
    const sw = (crop.width / 100) * naturalW;
    const sh = (crop.height / 100) * naturalH;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      Math.round(sx), Math.round(sy),
      Math.round(sw), Math.round(sh),
      0, 0,
      Math.round(sw), Math.round(sh)
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        onCrop(file, URL.createObjectURL(blob));
      }
    }, 'image/jpeg', 0.92);
  }, [crop, onCrop]);

  return (
    <div className="img-cropper-overlay" onClick={onCancel}>
      <div className="img-cropper-modal" onClick={(e) => e.stopPropagation()}>
        <div className="img-cropper-header">
          <h3>{title}</h3>
          <button type="button" className="img-cropper-close" onClick={onCancel}><FaTimes /></button>
        </div>
        {subtitle && (
          <p style={{ margin: 0, padding: '12px 20px', fontSize: '13px', color: '#555', lineHeight: 1.4 }}>{subtitle}</p>
        )}
        <div className="img-cropper-body">
          {aspectRatio && (
            /* Plain BLOCK wrapper: the body is a flex container, so anything
               placed directly in it becomes a flex item and inherits iOS
               WebKit's flex mis-layout (which clipped the hint off the left).
               Nesting the controls in a normal block div keeps them in plain
               block flow, immune to that quirk. */
            <div className="img-cropper-controls">
              <div className="img-cropper-mode-row">
                <button
                  type="button"
                  className={`img-cropper-mode-btn ${!freeCrop ? 'is-active' : ''}`}
                  onClick={() => setFreeCrop(false)}
                >
                  Standard card
                </button>
                <button
                  type="button"
                  className={`img-cropper-mode-btn ${freeCrop ? 'is-active' : ''}`}
                  onClick={() => setFreeCrop(true)}
                >
                  Free crop
                </button>
              </div>
              <span className="img-cropper-mode-hint">
                {freeCrop
                  ? 'Drag each edge independently — leave the sides alone to keep full width.'
                  : 'Locked to 2.5 : 3.5 trading card ratio.'}
              </span>
            </div>
          )}
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            aspect={freeCrop ? undefined : (aspectRatio || undefined)}
            className="img-cropper-reactcrop"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop"
              onLoad={onImageLoad}
              className="img-cropper-image"
            />
          </ReactCrop>
        </div>
        <div className="img-cropper-footer">
          <button type="button" onClick={onCancel} className="img-cropper-btn img-cropper-btn-cancel">{cancelLabel}</button>
          <button type="button" onClick={handleApply} className="img-cropper-btn img-cropper-btn-save">
            <FaCheck /><span>Apply</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
